// A small helper for the writers that change objects already in a file: it opens the document, hands out new object numbers, and
// appends what was written as one incremental update (ISO 32000-1 §7.5.6) with its own cross-reference section.

import { PdfDocument } from "./document";
import { Dict, PRef, type PObj } from "./parse";
import { Out, serialize, writeXref, deflate } from "./writer";

export interface UpdateCtx {
    doc: PdfDocument;
    root: PRef;
    newRef(): number;
    writeObj(num: number, body: string | Uint8Array, gen?: number): void;
    writeStream(num: number, dict: string, data: Uint8Array, compress?: boolean): Promise<void>;
    /** A copy of the dictionary object `num` that can be changed and written back with `put`. */
    copy(num: number): Dict;
    /** Writes `dict` as object `num`. */
    put(num: number, dict: Dict): void;
    /** A page's dictionary object number. */
    pageRef(index: number): PRef | null;
    /** The document information dictionary's object number; one is made (and put in the trailer) when the file has none. */
    ensureInfo(): number;
}

const enc = new TextEncoder();

/** Runs `fn` against the document and returns the original bytes with its writes appended. Returns `original` when nothing was written. */
export async function incrementalUpdate(original: Uint8Array, fn: (c: UpdateCtx) => Promise<void> | void): Promise<Uint8Array> {
    const doc = PdfDocument.open(original);
    if (doc.xref.trailer.has("Encrypt")) throw new Error("This PDF is encrypted, so it can't be changed here.");
    const xref = doc.xref;
    const root = xref.trailer.raw("Root");
    if (!(root instanceof PRef)) throw new Error("The PDF has no catalog reference.");
    doc.pages();
    let nextNum = Math.max(xref.trailer.num("Size"), ...[...xref.entries.keys()].map((k) => k + 1));
    const out = new Out();
    out.push(original);
    if (original[original.length - 1] !== 10) out.text("\n");
    const offsets: Array<[number, number]> = [];
    /** The dictionaries written so far, so a second change to an object starts from the first. */
    const latest = new Map<number, Dict>();
    const ctx: UpdateCtx = {
        doc, root,
        newRef: () => nextNum++,
        writeObj(num, body, gen = 0) {
            const at = offsets.findIndex((o) => o[0] === num);
            if (at >= 0) offsets.splice(at, 1); // the later write wins; the older body stays in the file unreferenced
            offsets.push([num, out.length]);
            out.text(`${num} ${gen} obj\n`);
            if (typeof body === "string") out.push(enc.encode(body)); else out.push(body);
            out.text("\nendobj\n");
        },
        async writeStream(num, dict, data, compress = true) {
            let payload = data, filter = "";
            if (compress && data.length > 80) { const z = await deflate(data); if (z && z.length < data.length) { payload = z; filter = "/Filter /FlateDecode "; } }
            offsets.push([num, out.length]);
            out.text(`${num} 0 obj\n<< ${dict} ${filter}/Length ${payload.length} >>\nstream\n`);
            out.push(payload);
            out.text("\nendstream\nendobj\n");
        },
        copy(num) {
            const d = latest.get(num) ?? xref.fetch(new PRef(num, 0));
            const c = new Dict(xref);
            if (d instanceof Dict) for (const k of d.keys()) c.set(k, d.raw(k) as PObj);
            return c;
        },
        put(num, dict) { latest.set(num, dict); ctx.writeObj(num, serialize(dict)); },
        pageRef: (i) => doc.pageRefs[i] ?? null,
        ensureInfo() {
            const cur = xref.trailer.raw("Info");
            if (cur instanceof PRef) return cur.num;
            const n = nextNum++;
            ctx.writeObj(n, "<< >>");
            xref.trailer.set("Info", new PRef(n, 0));
            return n;
        }
    };
    await fn(ctx);
    if (!offsets.length) return original;
    writeXref(out, offsets, nextNum, original, xref, root);
    return out.bytes();
}
